import type { ReactNode } from 'react';

import { cn } from '@/lib/cn';

import { Hero } from './Hero';
import { Topbar } from './Topbar';

export interface AppShellProps {
  children: ReactNode;
  className?: string;
}

/**
 * The application frame: navy `Topbar`, the `Hero` vision band, a 1280px-max
 * content column on the canvas ground, and the playbook citation footer.
 *
 * The storyboard draws each page inside a white `.frame` card because it is a
 * storyboard; at app scale the frame becomes the viewport, so the surface
 * treatment lives on the panels inside a page instead.
 *
 * The vision band is a deliberate departure from the storyboard, which has no
 * hero on any of its eight pages. It sits in the shell rather than on page 1
 * so the statement holds across every route, including `/meeting` — which
 * still renders inside this layout until B3 lifts it into its own full-screen
 * one, at which point the band drops off the projector view by itself.
 */
export function AppShell({ children, className }: AppShellProps) {
  return (
    <div className={cn('flex min-h-screen flex-col bg-canvas', className)}>
      <Topbar />
      <Hero />
      <main className="mx-auto w-full max-w-board flex-1 px-4 py-4">{children}</main>
      <footer className="mx-auto w-full max-w-board px-4 pb-6 pt-2 text-2xs text-text-muted">
        JCORP AI · Execution Dashboard · governed by{' '}
        <span className="font-semibold">G-PB-JCORP-AIX-v1.0</span>
      </footer>
    </div>
  );
}
